import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

// ── Types ──
export interface UserPerms {
  entities: string[]
  years: string[]
  access: 'view' | 'edit'
}

export interface User {
  id: string
  email: string
  password: string
  name: string
  role: 'admin' | 'editor' | 'viewer'
  permissions: UserPerms
}

interface AuthContextValue {
  user: User | null
  setUser: (u: User | null) => void
  login: (authUser: { id: string; name: string; email: string; role: string }, token: string) => void
  logout: () => void
  isAdmin: boolean
  canEdit: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/**
 * Resolves the initial user from localStorage or dev bypass.
 * Accepts current ENTITIES/YEARS for permissions hydration.
 */
function clearAuthStorage() {
  try {
    // fin_sso_user / fin_auth_token are legacy keys from the Portal SSO era;
    // still cleared so old browser sessions don't linger after the cutover.
    localStorage.removeItem('fin_sso_user')
    localStorage.removeItem('fin_auth_token')
    localStorage.removeItem('fin_auth_user')
  } catch {}
}

function resolveInitialUser(entities: string[], years: string[]): User | null {
  // The session token lives in an httpOnly `fin_session` cookie and is
  // never readable from JS — XSS cannot exfiltrate it. We only cache the
  // user profile in localStorage for fast first paint; the effect below
  // re-validates the cookie against /api/auth/validate and drops to login
  // if it is missing or expired.
  try {
    const authStored = localStorage.getItem('fin_auth_user')
    if (authStored) {
      const u = JSON.parse(authStored)
      return {
        id: u.id, email: u.email, password: '', name: u.name,
        role: u.role === 'ADMIN' || u.role === 'admin' ? 'admin' as const : 'viewer' as const,
        permissions: { entities: [...entities], years: [...years], access: 'edit' as const },
      }
    }
    if (localStorage.getItem('fin_sso_user')) clearAuthStorage()
  } catch { /* corrupted localStorage */ }

  // Opt-in dev bypass. This used to apply to all of dev mode, which was
  // harmless while `npm run dev` served no API — but now that it does, a faked
  // user is worse than no user: the app looks signed in and every request comes
  // back 401, which reads as the data being broken rather than the session
  // missing. Dev signs in for real by default; set VITE_AUTH_BYPASS=1 to work
  // on the UI with no database behind it. Production builds never reach this
  // branch — import.meta.env.DEV is false at build time.
  if (import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === '1') {
    return {
      id: 'dev', email: 'dev@localhost', password: '', name: 'Dev User',
      role: 'admin' as const,
      permissions: { entities: [...entities], years: [...years], access: 'edit' as const },
    }
  }
  return null
}

interface AuthProviderProps {
  children: ReactNode
  /** Current entities list (updated when data loads) */
  entities: string[]
  /** Current years list (updated when data loads) */
  years: string[]
}

export function AuthProvider({ children, entities, years }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(() => resolveInitialUser(entities, years))

  // Update permissions when entities/years change (data reload)
  useEffect(() => {
    setUser(prev => {
      if (!prev) return prev
      return { ...prev, permissions: { ...prev.permissions, entities: [...entities], years: [...years] } }
    })
  }, [entities, years])

  // Resolve the session from the httpOnly `fin_session` cookie on mount.
  //
  // This is the single entry point for both sign-in paths: the credentials
  // flow sets the cookie via /api/auth/verify-2fa and calls login() directly,
  // while Entra sign-in returns from Microsoft with the cookie already set by
  // /api/auth/entra/callback and no client-side state at all. Validating here
  // covers the Entra case and expires stale sessions in the credentials case.
  //
  // Skipped only under the bypass, where the injected user has no real cookie
  // to validate. Ordinary dev now validates exactly as production does.
  useEffect(() => {
    if (import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === '1') return
    fetch('/api/auth/validate', { credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) {
          clearAuthStorage()
          setUser(null)
          return
        }
        const { user: u } = await res.json()
        localStorage.setItem('fin_auth_user', JSON.stringify(u))
        setUser({
          id: u.id, email: u.email, password: '', name: u.name,
          role: u.role === 'ADMIN' || u.role === 'admin' ? 'admin' as const : 'viewer' as const,
          permissions: { entities: [...entities], years: [...years], access: 'edit' as const },
        })
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback((authUser: { id: string; name: string; email: string; role: string }, _token: string) => {
    // Cache the profile for fast first paint on reload. The session itself
    // lives only in the httpOnly cookie — nothing sensitive is stored here.
    try {
      localStorage.setItem('fin_auth_user', JSON.stringify(authUser))
    } catch { /* private browsing / quota */ }

    const u: User = {
      id: authUser.id, email: authUser.email, password: '', name: authUser.name,
      role: authUser.role === 'ADMIN' || authUser.role === 'admin' ? 'admin' : 'viewer',
      permissions: { entities: [...entities], years: [...years], access: 'edit' },
    }
    setUser(u)
  }, [entities, years])

  const logout = useCallback(() => {
    setUser(null)
    clearAuthStorage()
    fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
  }, [])

  const isAdmin = user?.role === 'admin'
  const canEdit = user?.permissions?.access === 'edit' || isAdmin

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, isAdmin, canEdit }}>
      {children}
    </AuthContext.Provider>
  )
}
